/**
 * @puffin-core/auth-client — device-PIN + backup-code operations (pos superset).
 *
 * All throw-style. Token sourcing: `registerDeviceWithPin` takes an explicit
 * pre-auth token (device flow); `registerDevice`, `getDevices`, and
 * `revokeDevice` attach the stored token — device listing/revocation are
 * user-scoped operations behind `authenticateToken` (revocation additionally
 * sits behind the AUTH-FRESH-001 `device:remove` step-up gate on pos-api);
 * the rest send no auth header (legacy pre-auth behavior).
 *
 * DEVICE-AUTH-UX-001 (2026-07-11): getDevices/revokeDevice previously used
 * `auth: 'none'` and returned the raw envelope against a `/devices` path no
 * Colony API ever implemented — the ops were dead endpoints. They now
 * authenticate, unwrap the canonical RES-ENV-001 envelope, and consuming apps
 * point them at their real routes via the factory `endpoints` override
 * (pos-web: `/device-pin/devices` + `/device`).
 *
 * @module @puffin-core/auth-client/operations/devices
 */

import { extractData, authError, networkError } from '../internal.js';

const NETWORK_ERROR_RETRY = 'Network error. Please check your connection and try again.';
const NETWORK_ERROR_SHORT = 'Network error. Please check your connection.';

/**
 * @param {Object} ctx
 * @returns {Object} device methods
 */
export function buildDeviceOperations(ctx) {
  const { request, endpoints } = ctx;

  // `locationId`: the location the device is bound to (pos-api reads it, and it scopes the PIN's uniqueness).
  // LIFE-SHORTLISTED-001 (2026-10-11): the body was rebuilt without it, so the wizard's chosen location never
  // reached the server.
  // SECREQ S6c (d) N1: `deviceType` (a new register's type asks its own key) and `deviceToken` (the register's own
  // identity, the one PIN sign-in sends) were dropped from the body too. Each rides when given; an absent one stays
  // absent, so a caller that has none sends exactly what it did.
  async function registerDeviceWithPin({
    token,
    pin,
    deviceFingerprint,
    deviceName,
    locationId,
    deviceType,
    deviceToken,
  } = {}) {
    const res = await request({
      method: 'POST',
      path: endpoints.registerDevicePin,
      body: {
        pin,
        deviceFingerprint,
        deviceName,
        locationId,
        ...(deviceType === undefined ? {} : { deviceType }),
        ...(deviceToken === undefined ? {} : { deviceToken }),
      },
      auth: { token },
    });
    if (res.networkError) throw networkError(NETWORK_ERROR_RETRY);
    if (!res.ok) throw authError(res, 'Device registration failed');
    return extractData(res.body);
  }

  async function registerDevice({ userId, pin, deviceFingerprint, deviceName } = {}) {
    const res = await request({
      method: 'POST',
      path: endpoints.registerDevicePin,
      body: { userId, pin, deviceFingerprint, deviceName },
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR_SHORT);
    if (!res.ok) throw authError(res, 'Registration failed');
    return res.body;
  }

  async function getDevices(userId) {
    const res = await request({
      method: 'GET',
      path: endpoints.getDevices,
      query: { userId },
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR_SHORT);
    if (!res.ok) throw authError(res, 'Failed to fetch devices');
    return extractData(res.body);
  }

  async function revokeDevice(deviceId) {
    const res = await request({
      method: 'DELETE',
      path: `${endpoints.revokeDevice}/${deviceId}`,
      auth: 'storage',
    });
    if (res.networkError) throw networkError(NETWORK_ERROR_SHORT);
    if (!res.ok) throw authError(res, 'Failed to revoke device');
    return extractData(res.body);
  }

  return { registerDeviceWithPin, registerDevice, getDevices, revokeDevice };
}
